import { useEffect, useRef } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import storeWrapper, { persistor, store } from "../utils/store/storeWrapper";
import preventer from "../utils/common/preventer";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "codemirror/lib/codemirror.css";
import "@toast-ui/editor/dist/toastui-editor.css";
import "../styles/global.scss";
import "../styles/messenger.scss";
import "../styles/uprism.scss";
import "../styles/calendar.scss";
import "../styles/datePicker.scss";
import "../styles/editor.scss";
import { useRoomConnection } from "../utils/customHook/useRoomConnection";
import { SOCKET_EVENT } from "../utils/data/socket.enum";
import { RTCConfig } from "../utils/data/rtcConfig";
import { useCreateMediaStream } from "../utils/customHook/useCreateMediaStream";
import { socketId } from "../utils/data/socketID";
import {
  setCamStreamState,
  setScreenStreamState,
} from "../utils/store/actions/mediaStreamAction";
import { VideoStreamMerger } from "video-stream-merger";
import { useRouter } from "next/router";
import { setSocketViewerInfo } from "../utils/store/actions/socketAction";
import { PersistGate } from "redux-persist/integration/react";
import { receiveStatus } from "../utils/common/receiveStatus";
import { setCheck } from "../utils/store/actions/userStateAction";
import { useNetworkMonitor } from "../utils/customHook/useNetworkMonitor";
import { useNetworkPauseTimer } from "../utils/customHook/useNetworkPauseTimer";
import NetworkAlert from "../components/NetworkAlert/NetworkAlert";

let my_video_stream = null;
let resize_video_stream = null;

// 합성 된 stream을 담을 변수
let my_mix_stream;

// main_display 초기설정
let main_display = "CAMERA";

// 현재 비디오 화질(해상도 계산)
let my_video_quality = 2;

// 비디오 크기를 담을 변수
let video_width;
let video_height;

// 스크린 크기를 담을 변수
let screen_width;
let screen_height;

let peerConnections = [];
let peerSocketId = [];

let isShareViewFlag = false;

const App = ({ Component, pageProps }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const timeOutDisconnected = useRef();
  const viewerRef = useRef(null);
  const videoRef = useRef(null);
  const screenRef = useRef(null);
  const userInfo = useSelector((state) => state.userInfo);
  const { joinRoom, socket, sendToExamTopic } = useRoomConnection();
  const { video, audio, videoStream, screenStream } = useSelector(
    (state) => state.device
  );
  const { myRoomId, myPeerId, con_my_peer_type, peerViewMod } = socketId(
    userInfo?.ROOM_IDX,
    userInfo?.Tester_IDX
  );
  const socketData = useSelector((state) => state.socket);
  const { getWebcam, getScreen, getAudio } = useCreateMediaStream();
  const userState = useSelector((state) => state.userState);

  receiveStatus();
  useNetworkMonitor(socket);
  useNetworkPauseTimer();

  useEffect(() => {
    preventer();
  }, []);

  const mergeVideoStream = async (videoStream) => {
    if (videoRef.current) {
      videoRef.current.srcObject = videoStream;

      my_video_stream = videoStream;
      await new Promise(
        (resolve) => (videoRef.current.onloadedmetadata = resolve)
      );
      video_width = videoRef.current.videoWidth;
      video_height = videoRef.current.videoHeight;

      joinRoom();
      //Create a new video merger.
      if (main_display) {
        //CAMERA
        resize_video_stream = new VideoStreamMerger({
          width: 160,
          height: 120,
          fps: 4,
        });
        // Start the merging.
        resize_video_stream.start();
        my_mix_stream = resize_video_stream.result;

        resizeVideoStream(main_display, my_video_quality);
      }
    }
  };

  useEffect(() => {
    if (
      !userInfo.isPreCheck &&
      userState.isCheckDevice &&
      screenStream?.id &&
      videoStream?.id
    ) {
      isShareViewFlag = true;
      screenRef.current.srcObject = screenStream;
      new Promise((resolve) => (screenRef.current.onloadedmetadata = resolve));
      screen_width = screenRef.current.clientWidth;
      screen_height = screenRef.current.clientHeight;

      videoRef.current.srcObject = videoStream;

      if (!userInfo.isPreCheck) {
        mergeVideoStream(videoStream);
      }
    }
  }, [
    screenStream,
    videoStream,
    socketData?.viewerSocket?.viewerSocketId,
    userState.isCheckDevice,
  ]);

  useEffect(() => {
    if (
      !isShareViewFlag &&
      router.pathname !== "/" &&
      router.pathname !== "/prevInfo" &&
      router.pathname !== "/device" &&
      router.pathname !== "/register" &&
      router.pathname !== "/cheat" &&
      !screenStream?.id &&
      !videoStream?.id
    ) {
      isShareViewFlag = true;
      getScreen((stream) => {
        screenRef.current.srcObject = stream;
        new Promise(
          (resolve) => (screenRef.current.onloadedmetadata = resolve)
        );
        screen_width = screenRef.current.clientWidth;
        screen_height = screenRef.current.clientHeight;
        dispatch(setScreenStreamState(stream));
        getWebcam((stream) => {
          videoRef.current.srcObject = stream;
          dispatch(setCamStreamState(stream));
          if (!userInfo.isPreCheck) {
            mergeVideoStream(stream);
          }
        });
      });
    }
    return () => {
      socket?.off(SOCKET_EVENT.EXAM);
    };
  }, [router.pathname, socketData?.viewerSocket?.viewerSocketId]);

  const deletePeerConnection = () => {
    if (Object.keys(peerConnections).length > 0) {
      Object.keys(peerConnections).forEach((peerId) => {
        peerConnections[peerId].close();
        delete peerConnections[peerId];
      });
    }
  };

  const closePeerConnection = (peerId) => {
    const pc = peerConnections[peerId];
    if (!pc) return;

    pc.close();
    delete peerConnections[peerId];
  };

  const setPeerRemoteAnswer = async (peerId, description) => {
    const pc = peerConnections[peerId];

    if (!pc || !description) return;

    if (pc.currentRemoteDescription?.sdp === description.sdp) {
      return;
    }

    if (pc.signalingState !== "have-local-offer") {
      console.warn("Skipping remote answer in invalid signaling state", {
        peerId,
        signalingState: pc.signalingState,
        descriptionType: description.type,
      });
      return;
    }

    await pc.setRemoteDescription(description);
  };

  // 비디오 사이즈 변경 함수
  const resizeVideoStream = async (mainStream, quality) => {
    let resize_width;
    let resize_height;

    let resize_video_width;
    let resize_video_height;
    let resize_screen_width;
    let resize_screen_height;

    if (mainStream === "SCREEN") {
      quality = quality + 2;
    }

    resize_width = 80 * quality;
    resize_height = 60 * quality;

    resize_video_width = 80 * quality;
    resize_video_height = 45 * quality;

    resize_screen_width = 80 * quality;
    resize_screen_height = 45 * quality;

    resize_video_stream.setOutputSize(resize_width, resize_height);
    resize_video_stream._frameCount = 0;

    if (mainStream === "CAMERA" || mainStream === "CAMERA-ONLY") {
      resize_video_stream.addStream(videoRef.current.srcObject, {
        x: 0,
        y: resize_height - resize_video_height,
        width: resize_video_width,
        height: resize_video_height,
        mute: false,
      });

      if (mainStream === "CAMERA" && screenRef.current.srcObject) {
        resize_video_stream.addStream(screenRef.current.srcObject, {
          x: 0,
          y: 0,
          width: resize_screen_width * 0.45,
          height: resize_screen_height * 0.45,
          mute: true,
        });
      }
    } else if (mainStream === "SCREEN" || mainStream === "SCREEN-ONLY") {
      resize_video_stream.addStream(screenRef.current.srcObject, {
        x: 0,
        y: resize_height - resize_screen_height + resize_screen_height * 0.1,
        width: resize_screen_width,
        height: resize_screen_height - resize_screen_height * 0.1,
        mute: true,
      });

      if (mainStream === "SCREEN-ONLY") {
        resize_video_width = 1;
        resize_video_height = 1;
      }
      resize_video_stream.addStream(videoRef.current.srcObject, {
        x: 0,
        y: 0,
        width: resize_video_width * 0.45,
        height: resize_video_height * 0.45,
        mute: false,
      });
    }
    if (resize_video_stream._streams.length > 2) {
      resize_video_stream._streams.splice(0, 2);
    }
  };

  useEffect(() => {
    if (!socket) return;
    //영상 위치/화질 변경
    socket?.on(SOCKET_EVENT.USER_VIDEO, async (message) => {
      let videoView = message.videoView;
      let videoQuality = message.videoQuality;

      if (my_mix_stream?.id) {
        if (videoQuality > 0) {
          if (my_mix_stream.getVideoTracks()[0].enabled === false) {
            my_mix_stream.getVideoTracks()[0].enabled = true;
          }
          await resizeVideoStream(videoView, videoQuality);
        } else {
          my_mix_stream.getVideoTracks()[0].enabled = false;
        }
      }
    });

    //사용자 ID 수신
    socket?.on(SOCKET_EVENT.USER_ID, (message) => {
      const conPeerId = message.peerId;
      const conPeerSocketId = message.socketId;
      const conPeerType = message.peerType;

      if (conPeerType === "MANAGER") {
        //감독관의 peerId, socketId 저장
        dispatch(
          setSocketViewerInfo({
            viewerPeerId: conPeerId,
            viewerSocketId: conPeerSocketId,
            viewerPeerType: conPeerType,
          })
        );
      }
    });

    socket?.on(
      SOCKET_EVENT.USER_OFFER,
      async (peerId, socketId, videoAnswer, description) => {
        const viewMode = "CAMERA";
        const videoQuality = 2;

        if (main_display !== viewMode || my_video_quality !== videoQuality) {
          //메인 디스플레이 저장(CAMERA / SCREEN / CAMERA-ONLY / SCREEN-ONLY)
          main_display = viewMode;
          my_video_quality = videoQuality; //현재 화질을 저장
          //전송될 영상 사이즈 변경
          resizeVideoStream(viewMode, videoQuality);
        }

        closePeerConnection(peerId);

        //연결점을 만든다.
        peerConnections[peerId] = new RTCPeerConnection(RTCConfig);

        //영상 교환일 경우에만
        if (videoAnswer === "Y") {
          //내 영상과 오디오에서 오는 stream을 가져다가 연결을 만든다.
          //peer to peer 연결 안에다가 영상과 오디오를 넣는다.
          my_mix_stream
            .getTracks()
            .forEach((track) =>
              peerConnections[peerId].addTrack(track, my_mix_stream)
            );
        }

        peerConnections[peerId].oniceconnectionstatechange = function () {
          const pc = peerConnections[peerId];
          if (!pc) return;
          console.log("ICE state...: ", pc.iceConnectionState);
          if (
            pc.iceConnectionState === "failed" ||
            pc.iceConnectionState === "disconnected"
          ) {
            try {
              if (typeof pc.restartIce === "function") {
                pc.restartIce();
              }
            } catch (e) {
              console.warn("ICE restart failed", e);
            }
          }
        };

        try {
          await peerConnections[peerId].setRemoteDescription(description);
          const sdp = await peerConnections[peerId].createAnswer();
          await peerConnections[peerId].setLocalDescription(sdp);

          socket.emit(
            SOCKET_EVENT.ANSWER,
            myPeerId,
            socketId,
            peerConnections[peerId].localDescription
          );
        } catch (error) {
          console.error("Failed to handle remote offer", error);
        }

        peerConnections[peerId].ontrack = (event) => {
          let stream = event.streams[0];
          if (stream) {
            if (event.track.kind === "video") {
              //감독관 영상 출력
              viewerRef.current.srcObject = stream;
            }
          }
        };

        peerConnections[peerId].onicecandidate = (event) => {
          if (event.candidate && userInfo?.Tester_IDX && userInfo?.ROOM_IDX) {
            socket.emit(
              SOCKET_EVENT.CANDIDATE,
              myPeerId,
              socketId,
              event.candidate
            );
          }
        };
      }
    );

    socket?.on(SOCKET_EVENT.USER_CANDIDATE, (peerId, candidate) => {
      const pc = peerConnections[peerId];

      if (!pc) return;

      pc.addIceCandidate(new RTCIceCandidate(candidate)).catch((error) => {
        console.error("Failed to add ICE candidate", error);
      });
    });

    socket?.on(SOCKET_EVENT.VIDEO_CALLED, (peerId, socketId, message) => {
      peerSocketId[peerId] = socketId;

      const sendVideo = message.sendVideo;
      const getVideo = message.getVideo;

      closePeerConnection(peerId);

      peerConnections[peerId] = new RTCPeerConnection(RTCConfig);

      if (getVideo === "Y") {
        my_mix_stream
          ?.getTracks()
          .forEach((track) =>
            peerConnections[peerId].addTrack(track, my_mix_stream)
          );
      } else {
        my_mix_stream
          .getTracks()
          .forEach((track) => peerConnections[peerId].addTrack(track));
      }
      peerConnections[peerId].onicecandidate = (event) => {
        if (event.candidate && userInfo?.Tester_IDX && userInfo?.ROOM_IDX) {
          socket.emit(
            SOCKET_EVENT.CANDIDATE,
            myPeerId,
            socketId,
            event.candidate
          );
        }
      };

      peerConnections[peerId]
        .createOffer()
        .then((sdp) => peerConnections[peerId].setLocalDescription(sdp))
        .then(() => {
          socket.emit(
            SOCKET_EVENT.OFFER,
            myPeerId,
            socketId,
            sendVideo,
            peerConnections[peerId].localDescription
          );
        })
        .catch((error) => {
          console.error("Failed to create local offer", error);
        });
      peerConnections[peerId].oniceconnectionstatechange = function () {
        const pc = peerConnections[peerId];
        if (!pc) return;
        console.log("ICE state: ", pc.iceConnectionState);
        if (
          pc.iceConnectionState === "failed" ||
          pc.iceConnectionState === "disconnected"
        ) {
          try {
            if (typeof pc.restartIce === "function") {
              pc.restartIce();
            }
          } catch (e) {
            console.warn("ICE restart failed", e);
          }
        }
      };
    });

    socket?.on(SOCKET_EVENT.USER_ANSWER, async (peerId, description) => {
      try {
        await setPeerRemoteAnswer(peerId, description);

        if (!peerConnections[peerId]) return;

        peerConnections[peerId].ontrack = (event) => {
          let stream = event.streams[0];

          if (stream) {
            if (event.track.kind === "video") {
              viewerRef.current.srcObject = stream;
            }
          }
        };
      } catch (error) {
        console.error("Failed to handle remote answer", error);
      }
    });

    socket?.on(SOCKET_EVENT.USER_REFRESH, () => {
      socket.close();
      //소켓 접속
      socket.connect();
      //룸 접속
      joinRoom();
    });

    //소켓 재연결 이벤트시 (끊겼다가 다시 접속)
    socket.io.on("reconnect", () => {
      socket.close();
      socket.connect(); //소켓 접속
      deletePeerConnection();
      joinRoom(); // 룸 접속
    });

    //사용자 룸 입장
    socket?.on(SOCKET_EVENT.USER_CONNECTED, (message) => {
      clearTimeout(timeOutDisconnected.current);

      const conPeerId = message.peerId;
      const conPeerType = message.peerType;
      const conPeerSocketId = message.socketId;
      const getVideo = "Y";

      if (
        conPeerId ===
        `${process.env.NEXT_PUBLIC_SITE_ID}` +
          "-" +
          userInfo.ROOM_IDX +
          "-MANAGER"
      ) {
        //응시자 접속이 아니라면(감독관 또는 운영자)
        socket?.emit(SOCKET_EVENT.SEND_ID, {
          peerId: myPeerId,
          peerType: con_my_peer_type,
          targetSocketId: conPeerSocketId,
        });
        dispatch(
          setSocketViewerInfo({
            viewerPeerId: conPeerId,
            viewerSocketId: conPeerSocketId,
            viewerPeerType: conPeerType,
          })
        );
      }
    });

    //소켓 재연결 이벤트시
    socket?.on(SOCKET_EVENT.VIDEO_REFRESH, () => {
      deletePeerConnection();
      joinRoom();
    });

    return () => {
      socket?.off(SOCKET_EVENT.USER_CONNECTED);
      socket?.off(SOCKET_EVENT.USER_REFRESH);
      socket?.off(SOCKET_EVENT.USER_VIDEO);
      socket?.off(SOCKET_EVENT.USER_ID);
      socket?.off(SOCKET_EVENT.USER_OFFER);
      socket?.off(SOCKET_EVENT.USER_ANSWER);
      socket?.off(SOCKET_EVENT.VIDEO_CALLED);
      socket?.off(SOCKET_EVENT.USER_CANDIDATE);
      socket?.off(SOCKET_EVENT.USER_SOUND);
      socket?.off(SOCKET_EVENT.VIDEO_REFRESH);
      socket?.io?.off("reconnect");
    };
  }, [userInfo?.Tester_IDX, userInfo?.ROOM_IDX]);

  useEffect(() => {
    if (!socket) return;
    socket?.on(SOCKET_EVENT.USER_DISCONNECTED, (peerId, peerType, socketId) => {
      //응시자가 아니라면(감독관 또는 운영자)
      if (peerType !== "TESTER") {
        if (peerSocketId[peerId] === socketId) {
          if (peerConnections[peerId]) {
            //커넥션 ID 삭제
            peerConnections[peerId].close();
            delete peerConnections[peerId];
            //감독관이 끊겼을 경우
            if (peerType === "MANAGER") {
              //감독관 웹캠 OFF
              viewerRef.current.srcObject = null;
              //검사 진행 중 한정
              if (router.pathname === "/main") {
                timeOutDisconnected.current = setTimeout(() => {
                  dispatch(setCheck(false));
                  sendToExamTopic(
                    socketData?.viewerSocket?.viewerSocketId,
                    "PAUSE"
                  );
                  dispatch(
                    setSocketViewerInfo({
                      viewerPeerId: null,
                      viewerSocketId: null,
                      viewerPeerType: null,
                    })
                  );
                }, 180000);
              }
            }
          }
        }
      }
    });
    return () => {
      socket?.off(SOCKET_EVENT.EXAM);
      socket?.off(SOCKET_EVENT.USER_DISCONNECTED);
    };
  }, [socket, router]);

  useEffect(() => {
    if (!socket) return;
    //음성 대화 연결
    socket?.on(SOCKET_EVENT.USER_VOICE_CHAT, (message) => {
      if (viewerRef.current) {
        if (message.message === "ON") {
          viewerRef.current.muted = false;
        } else {
          viewerRef.current.muted = true;
        }
      }
    });
    return () => {
      if (Object.keys(socket).length === 0) return;
      socket.off(SOCKET_EVENT.USER_VOICE_CHAT);
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={null}>
        <NetworkAlert />
        <Component {...pageProps} />
        <div
          style={{
            position: "fixed",
            zIndex: -1000000,
            top: 0,
            right: 0,
            height: "100px",
          }}
        >
          <video ref={videoRef} muted autoPlay style={{ width: "10px" }} />
          <video ref={viewerRef} muted autoPlay style={{ width: "10px" }} />
          <video ref={screenRef} muted autoPlay style={{ width: "10px" }} />
        </div>
      </PersistGate>
    </Provider>
  );
};

export default storeWrapper.withRedux(App);
